import React, { useState } from "react";
import { Link } from "react-router-dom";
import { whoIHelp, siteConfig, featuredIndustries, industryCategories } from "../data/mockData";
import {
  Building2, Briefcase, Heart, User, Stethoscope, Truck, Wrench, Scale,
  Monitor, Palette, TrendingUp, Home, Gavel, HeartPulse, GraduationCap,
  Megaphone, UtensilsCrossed, Microscope,
  Check, Phone, Calendar, ArrowRight, ChevronDown,
} from "lucide-react";

const iconMap = {
  Building2, Briefcase, Heart, User, Stethoscope, Truck, Wrench, Scale,
  Monitor, Palette, TrendingUp, Home, Gavel, HeartPulse, GraduationCap,
  Megaphone, UtensilsCrossed, Microscope,
};

const detailedHelp = [
  {
    title: "Small Business Owners",
    icon: "Building2",
    description: "Running a small business is hard enough without having to navigate the complexities of health insurance. I work with small business owners to set up group health plans that attract and retain talent \u2014 without the enterprise-level price tag.",
    benefits: [
      "Group health plans scaled to your team size",
      "Tax advantages for employer-sponsored coverage",
      "Competitive benefits that help you attract top talent",
      "Simple enrollment and administration",
      "Ongoing support as your team grows",
    ],
  },
  {
    title: "Self-Employed Professionals",
    icon: "Briefcase",
    description: "Freelancers, consultants, contractors, and entrepreneurs \u2014 I understand the unique challenges of being your own boss. You need flexible, affordable coverage that doesn\u2019t require jumping through hoops.",
    benefits: [
      "Customizable plans for independent professionals",
      "Often 40% less expensive than marketplace alternatives",
      "Year-round enrollment \u2014 no waiting for open enrollment",
      "Coverage that travels with you nationwide",
      "Options for dental, vision, and supplemental coverage",
    ],
  },
  {
    title: "Families",
    icon: "Heart",
    description: "Your family\u2019s health is everything. I help families find comprehensive coverage that protects everyone under your roof \u2014 from preventive care for the kids to specialist visits for the parents.",
    benefits: [
      "Comprehensive family health plans",
      "Pediatric care and well-child visits",
      "Maternity and newborn coverage options",
      "Prescription drug coverage",
      "Mental health and wellness benefits",
    ],
  },
  {
    title: "Individuals",
    icon: "User",
    description: "Whether you\u2019re between jobs, aging off a parent\u2019s plan, early retired, or just looking for better coverage, I\u2019ll help you find an individual plan that fits your specific needs and budget.",
    benefits: [
      "Individual health plans from multiple carriers",
      "Options for every budget level",
      "Larger provider networks than marketplace plans",
      "No referral requirements for specialists",
      "Life and supplemental insurance options",
    ],
  },
];

/* ── Sub-components ── */

const QuickCards = () => (
  <section className="py-8 bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {whoIHelp.map((item) => {
          const Icon = iconMap[item.icon];
          return (
            <div key={item.id} className="bg-[#F1F8F2] rounded-xl p-6 text-center">
              <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center mx-auto mb-4">
                {Icon && <Icon size={28} className="text-[#3A7D44]" />}
              </div>
              <h3 className="font-bold text-[#1B2A1B]">{item.title}</h3>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

const DetailedHelpItem = ({ item, itemIndex }) => {
  const Icon = iconMap[item.icon];
  const isEven = itemIndex % 2 === 0;
  return (
    <div className="grid md:grid-cols-2 gap-12 items-center">
      <div className={!isEven ? "md:order-2" : ""}>
        <div className="w-14 h-14 bg-[#E8F5E9] rounded-xl flex items-center justify-center mb-5">
          {Icon && <Icon size={28} className="text-[#3A7D44]" />}
        </div>
        <h2 className="text-2xl font-bold text-[#1B2A1B] mb-4">{item.title}</h2>
        <p className="text-[#5A6B5A] leading-relaxed mb-6">{item.description}</p>
        <ul className="space-y-3 mb-6">
          {item.benefits.map((b) => (
            <li key={b} className="flex items-start gap-3">
              <Check size={16} className="text-[#3A7D44] mt-0.5 flex-shrink-0" />
              <span className="text-sm text-[#5A6B5A]">{b}</span>
            </li>
          ))}
        </ul>
        <a href={siteConfig.calendlyUrl} className="inline-flex items-center gap-2 text-[#3A7D44] font-semibold hover:gap-3 transition-all duration-200">
          Get a quote <ArrowRight size={16} />
        </a>
      </div>
      <div className={`bg-[#F1F8F2] rounded-2xl p-12 flex items-center justify-center ${!isEven ? "md:order-1" : ""}`}>
        {whoIHelp[itemIndex] && whoIHelp[itemIndex].image ? (
          <img src={whoIHelp[itemIndex].image} alt={item.title} className="w-full max-w-sm rounded-xl object-contain aspect-[4/3] bg-gray-50" />
        ) : (
          <div className="w-32 h-32 bg-[#3A7D44]/10 rounded-3xl flex items-center justify-center">
            {Icon && <Icon size={64} className="text-[#3A7D44]" />}
          </div>
        )}
      </div>
    </div>
  );
};

const BenefitIntro = () => (
  <section className="py-20 bg-[#F9F7F4]">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-12">
        <span className="text-sm font-semibold text-[#3A7D44] uppercase tracking-wider">
          Beyond Specific Professions
        </span>
        <h2 className="text-3xl sm:text-4xl font-bold text-[#1B2A1B] mt-3 mb-6" style={{ fontFamily: "'DM Sans', sans-serif" }}>
          Who Can Benefit From Private Health Insurance?
        </h2>
      </div>
      <div className="space-y-5 text-[#5A6B5A] leading-relaxed">
        <p>
          Beyond the specific professions and industries highlighted, private health insurance offers valuable benefits to individuals across many life circumstances. People experiencing career transitions, those approaching early retirement but not yet eligible for Medicare, recent college graduates no longer covered under parental plans, and individuals whose employers offer minimal or inadequate coverage can all find significant advantages in private insurance options.
        </p>
        <p>
          Private coverage proves particularly beneficial when seeking more comprehensive benefits, greater provider flexibility, nationwide coverage for frequent travelers, or personalized plans addressing specific health conditions.
        </p>
        <p>
          Additionally, those whose income fluctuates throughout the year, who work multiple part-time positions, or who need to bridge coverage gaps between employment situations often discover that customized private plans provide both the protection and flexibility their unique situations demand.
        </p>
      </div>
    </div>
  </section>
);

const FeaturedIndustriesSection = () => (
  <section className="py-20 bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center max-w-2xl mx-auto mb-16">
        <span className="text-sm font-semibold text-[#3A7D44] uppercase tracking-wider">
          Industries We Serve
        </span>
        <h2 className="text-3xl sm:text-4xl font-bold text-[#1B2A1B] mt-3 mb-4" style={{ fontFamily: "'DM Sans', sans-serif" }}>
          Tailored Coverage for Every Industry
        </h2>
        <p className="text-[#5A6B5A] leading-relaxed">
          We specialize in helping individuals tailor custom health insurance solutions for their unique situation.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {featuredIndustries.map((ind) => {
          const Icon = iconMap[ind.icon];
          return (
            <div key={ind.title} className="bg-[#F9F7F4] rounded-xl p-8 hover:shadow-lg transition-shadow duration-300 border border-transparent hover:border-[#3A7D44]/10">
              <div className="w-12 h-12 bg-[#E8F5E9] rounded-xl flex items-center justify-center mb-5">
                {Icon && <Icon size={24} className="text-[#3A7D44]" />}
              </div>
              <h3 className="text-lg font-bold text-[#1B2A1B] mb-3">{ind.title}</h3>
              <p className="text-sm text-[#5A6B5A] leading-relaxed">{ind.description}</p>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

const IndustryCategoryCard = ({ category }) => {
  const [isOpen, setIsOpen] = useState(false);
  const Icon = iconMap[category.icon];
  const previewCount = 5;

  return (
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden hover:border-[#3A7D44]/20 transition-colors">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-6 py-5 text-left hover:bg-[#F1F8F2]/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#E8F5E9] rounded-lg flex items-center justify-center flex-shrink-0">
            {Icon && <Icon size={20} className="text-[#3A7D44]" />}
          </div>
          <div>
            <h3 className="font-semibold text-[#1B2A1B]">{category.title}</h3>
            <p className="text-xs text-[#5A6B5A]">{category.items.length} specialties</p>
          </div>
        </div>
        <ChevronDown size={20} className={`text-[#3A7D44] transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {/* Preview items always shown */}
      <div className="px-6 pb-3">
        <div className="flex flex-wrap gap-2">
          {category.items.slice(0, previewCount).map((item) => (
            <span key={item} className="px-3 py-1 bg-[#F1F8F2] rounded-full text-xs text-[#5A6B5A]">
              {item}
            </span>
          ))}
          {!isOpen && category.items.length > previewCount && (
            <span className="px-3 py-1 bg-[#E8F5E9] rounded-full text-xs text-[#3A7D44] font-medium">
              +{category.items.length - previewCount} more
            </span>
          )}
        </div>
      </div>

      {/* Expanded list */}
      <div className={`overflow-hidden transition-all duration-300 ${isOpen ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0"}`}>
        <div className="px-6 pb-5">
          <div className="flex flex-wrap gap-2">
            {category.items.slice(previewCount).map((item) => (
              <span key={item} className="px-3 py-1 bg-[#F1F8F2] rounded-full text-xs text-[#5A6B5A]">
                {item}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const AllIndustriesSection = () => (
  <section className="py-20 bg-[#F9F7F4]">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center max-w-2xl mx-auto mb-12">
        <span className="text-sm font-semibold text-[#3A7D44] uppercase tracking-wider">
          Comprehensive Coverage
        </span>
        <h2 className="text-3xl sm:text-4xl font-bold text-[#1B2A1B] mt-3 mb-4" style={{ fontFamily: "'DM Sans', sans-serif" }}>
          Other Industries & Specialties
        </h2>
        <p className="text-[#5A6B5A] leading-relaxed">
          Here are some other common industries and situations I have extensive experience working with. Don't see yours? I can still help.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {industryCategories.map((cat) => (
          <IndustryCategoryCard key={cat.title} category={cat} />
        ))}
      </div>
    </div>
  </section>
);

const PageCTA = () => (
  <section className="py-20 bg-[#3A7D44]">
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: "'DM Sans', sans-serif" }}>
        Ready to Secure Your Personalized Coverage?
      </h2>
      <p className="text-white/80 mb-8">
        No matter your industry or situation, I'm here to help you find the right plan. Let's have a conversation.
      </p>
      <div className="flex flex-wrap justify-center gap-4">
        <a href={siteConfig.calendlyUrl} className="inline-flex items-center gap-2 px-8 py-4 text-base font-semibold text-[#3A7D44] bg-white rounded-lg hover:bg-gray-50 transition-colors">
          <Calendar size={18} />
          Book a Free Call
        </a>
        <a href={`tel:${siteConfig.phone}`} className="inline-flex items-center gap-2 px-8 py-4 text-base font-semibold text-white border-2 border-white/40 rounded-lg hover:bg-white/10 transition-colors">
          <Phone size={18} />
          {siteConfig.phoneFormatted}
        </a>
      </div>
    </div>
  </section>
);

/* ── Main Page ── */

const WhoIHelpPage = () => {
  return (
    <>
      {/* Hero */}
      <section className="pt-28 pb-16 bg-gradient-to-b from-[#F1F8F2] to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-sm font-semibold text-[#3A7D44] uppercase tracking-wider">Who I Help</span>
          <h1 className="text-4xl sm:text-5xl font-bold text-[#1B2A1B] mt-3 mb-6" style={{ fontFamily: "'DM Sans', sans-serif" }}>
            Insurance Solutions for Real People
          </h1>
          <p className="text-lg text-[#5A6B5A] leading-relaxed max-w-2xl mx-auto">
            I work with people who are tired of confusing insurance options and want someone in their corner. Here's who I serve.
          </p>
        </div>
      </section>

      <QuickCards />

      {/* Detailed Help Sections */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-20">
            {detailedHelp.map((item, index) => (
              <DetailedHelpItem key={item.title} item={item} itemIndex={index} />
            ))}
          </div>
        </div>
      </section>

      <BenefitIntro />
      <FeaturedIndustriesSection />
      <AllIndustriesSection />
      <PageCTA />
    </>
  );
};

export default WhoIHelpPage;
